import { useEffect, useState } from 'react';
import { AlertTriangle, Search } from 'lucide-react';
import { Button } from '../../components/Button';
import { Card, CardHeader } from '../../components/Card';
import { Badge } from '../../components/Badge';
import { Banner } from '../../components/Banner';
import { Input } from '../../components/Input';
import { Money } from '../../components/Money';
import { PageHeader } from '../../components/PageHeader';
import { useCashEntryCandidate } from '../../api/admin';
import { recordCashPayment, type RecordCashResult } from '../../api/billing';
import { formatDate } from '../../lib/format';

/** Manual cash payment — single-page form with business search and amount. */
export function CashEntry() {
  const { data: cashEntryCandidate } = useCashEntryCandidate();
  const [renew, setRenew] = useState(true);
  const [sendReceipt, setSendReceipt] = useState(true);
  const [amount, setAmount] = useState('');
  const [ref, setRef] = useState('Office receipt #4421');
  const [note, setNote] = useState('Cash received at Sylhet office');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<RecordCashResult | null>(null);

  useEffect(() => {
    if (cashEntryCandidate) setAmount(String(cashEntryCandidate.business.owedMinor / 100));
  }, [cashEntryCandidate]);

  if (!cashEntryCandidate) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-[1440px] mx-auto space-y-4" aria-busy="true">
        <div className="skeleton h-8 w-64 max-w-full rounded" />
        <div className="space-y-3">
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
          <div className="skeleton h-14 rounded-lg" />
        </div>
      </div>
    );
  }

  async function onRecord() {
    if (!cashEntryCandidate) return;
    const amountMinor = parseCashAmountMinor(amount);
    if (amountMinor === null) {
      setError('Enter a valid amount in BDT (a positive number).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await recordCashPayment({ businessId: cashEntryCandidate.business.id, amountMinor, method: 'manual_cash' });
      setDone(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Platform"
        title="Record cash payment"
        subtitle="Mark a subscription paid when the customer paid by cash off-platform."
      />

      <div className="space-y-5">
        <Card className="p-5 sm:p-6">
          <CardHeader title="Find the business" />

          <Input
            label="Business"
            required
            placeholder="Search business…"
            prefix={<Search className="w-4 h-4" />}
            defaultValue={`${cashEntryCandidate.business.name} (#${cashEntryCandidate.business.id.replace('biz_', '')})`}
          />

          <div className="mt-4 p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 border border-zinc-200 dark:border-zinc-700">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div>
                <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50">
                  {cashEntryCandidate.business.name}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                  Plan: {cashEntryCandidate.business.plan} · Past due since {formatDate(cashEntryCandidate.business.pastDueSince)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge tone="warning">Past due</Badge>
                <span className="text-sm">
                  <span className="text-zinc-500 dark:text-zinc-400 mr-1">Owed:</span>
                  <Money minor={cashEntryCandidate.business.owedMinor} hideCents tone="neutral" className="text-zinc-950 dark:text-zinc-50 font-medium" />
                </span>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Payment details" />
          <div className="space-y-4">
            <Input
              label="Amount (BDT)"
              required
              prefix={<span className="text-sm">৳</span>}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="Received on" required type="date" defaultValue="2026-05-03" />
              <div>
                <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Received by</label>
                <select className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700">
                  <option>Sayem K (you)</option>
                  <option>Rakib Ahmed</option>
                  <option>Other staff member…</option>
                </select>
              </div>
            </div>
            <Input label="Payment reference" value={ref} onChange={(e) => setRef(e.target.value)} hint="Office receipt #, bank slip, etc." />
            <div>
              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1.5">Note</label>
              <textarea
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="block w-full px-3 py-2.5 text-sm rounded-md bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 focus:outline-none focus:ring-2 focus:ring-brand resize-none"
              />
            </div>
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <CardHeader title="Effect" />
          <div className="space-y-2">
            <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input
                type="checkbox"
                checked={renew}
                onChange={(e) => setRenew(e.target.checked)}
                className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
              />
              <span>Renew subscription for one billing period (1 month)</span>
            </label>
            <label className="flex items-start gap-2.5 text-sm text-zinc-700 dark:text-zinc-200 cursor-pointer">
              <input
                type="checkbox"
                checked={sendReceipt}
                onChange={(e) => setSendReceipt(e.target.checked)}
                className="mt-0.5 rounded border-zinc-300 dark:border-zinc-600 text-brand focus:ring-brand"
              />
              <span>Send receipt to business by email</span>
            </label>
          </div>
        </Card>

        {error && <Banner tone="error" title="Couldn’t record the payment">{error}</Banner>}
        {done ? (
          <Banner tone="success" title="Payment recorded">
            Invoice <span className="mono text-xs">{done.invoiceNo}</span> created — the business is now{' '}
            <strong>{done.status}</strong>.
          </Banner>
        ) : (
          <Banner tone="info">
            This creates an invoice marked <strong>Paid (cash)</strong> and reactivates the business if it was suspended.
            A row is added to the audit log under <span className="mono text-xs">payment.cash</span>.
          </Banner>
        )}

        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost">Cancel</Button>
          <Button onClick={onRecord} loading={submitting} disabled={submitting || done !== null}>
            {done ? 'Recorded' : 'Record payment'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Parse the BDT amount field to integer minor units (poisha); null if not a positive number. */
export function parseCashAmountMinor(amountBdt: string): number | null {
  const n = Number(amountBdt.trim());
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}
